import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "tree-service-web-design-colorado",
  metaTitle: "Tree Service Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom tree service web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Tree service marketing", to: "/tree-service-marketing-colorado" },
  ],
  currentLabel: "Tree Service Web Design Colorado",
  eyebrow: "Tree Service Web Design — Colorado",
  h1: (
    <>
      Tree Service <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded tree service websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Tree Service websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first tree service sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Tree Service web design in Colorado",
    body: "Tree Service web design is the practice of building conversion-focused, mobile-first websites for tree service companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — emergency removals after wind/snow events." },
    { title: "Service pages per job", body: "Removal, trimming, stump grinding, emergency, lot clearing, deep root fertilization — each ranks independently." },
    { title: "Sticky click-to-call", body: "Phone on every scroll — most calls happen from the homeowner standing under the tree." },
    { title: "Insurance + ISA certification", body: "Certified arborist, ISA, insurance, and bonding badges above the fold." },
    { title: "Storm response messaging", body: "Real-time storm/emergency availability surfaced above the fold during weather events." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Emergency intent peaks after storms", body: "Wind, snow, and ice storms create demand spikes. A fast, ready site captures them." },
    { heading: "Insurance is the trust signal", body: "Homeowners hiring a tree service worry about damage liability first. Insurance proof must be above the fold." },
    { heading: "Certified arborist is a ranking moat", body: "ISA-certified arborist pages outrank generalists in commercial and residential queries." },
  ],
  relatedLinks: [
    { to: "/tree-service-marketing-colorado", label: "Tree service marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado tree service case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a tree service website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a tree service website?", a: "Typical timeline is 4–6 weeks for a 12–20 page tree service site with full service and service-area pages. Larger multi-location tree service companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs tree service companies?", a: "No — we build tree service sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Tree Service Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for tree service companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/tree-service-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
